import { FC, ReactNode, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import {
  DemandeValidationDto,
  DevisPhoto,
  DevisPhotoFrais,
  DevisPhotoGarantie,
  DevisPhotoVariable
} from '../../types/api';
import {
  formatValidationAmount,
  formatValidationDate,
  formatValidationNumber,
  getValidationDisplayState
} from './validationFormat';

interface Column<T> {
  label: string;
  value: (item: T) => string;
  /** False when a difference on this column alone does not make the line a change (e.g. a recomputed premium). */
  tracked?: boolean;
}

interface ComparedRow<T> {
  key: string;
  before?: T;
  after?: T;
}

/** Pairs the lines of the two snapshots by id, keeping the order of "before" then the added ones. */
const pairRows = <T,>(
  before: T[],
  after: T[] | undefined,
  keyOf: (item: T) => string
): ComparedRow<T>[] => {
  const rows: ComparedRow<T>[] = before.map(item => ({
    key: keyOf(item),
    before: item,
    after: after?.find(other => keyOf(other) === keyOf(item))
  }));
  after
    ?.filter(item => !before.some(other => keyOf(other) === keyOf(item)))
    .forEach(item => rows.push({ key: keyOf(item), after: item }));
  return rows;
};

interface PhotoTableProps<T> {
  title: string;
  rows: ComparedRow<T>[];
  /** The first column names the line (designation, label). */
  columns: Column<T>[];
  /** False when only the "before" snapshot exists: plain values, no comparison. */
  compare: boolean;
}

/** Link-like toggle used to expand / collapse parts of the panel. */
const ToggleButton: FC<{ open: boolean; label: string; onClick: () => void }> = ({ open, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
  >
    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    {label}
  </button>
);

const PhotoTable = <T,>({ title, rows, columns, compare }: PhotoTableProps<T>) => {
  const { t } = useTranslation(NsKey.Contractualization);
  const k = 'CONTRACTUALIZATION.CARDS.VALIDATION_FEEDBACK_PANEL';
  const [showAll, setShowAll] = useState(false);

  const renderCell = (row: ComparedRow<T>, column: Column<T>, isName: boolean): ReactNode => {
    const before = row.before ? column.value(row.before) : null;
    const after = row.after ? column.value(row.after) : null;
    if (!compare || isName) return (before ?? after) || '—';
    if (before === null) return <span className="font-semibold text-green-700">{after}</span>;
    if (after === null) return <span className="text-gray-400 line-through">{before}</span>;
    if (before === after) return before;
    return (
      <span className="whitespace-nowrap">
        <span className="text-gray-400 line-through">{before}</span>
        {' → '}
        <span className="font-semibold text-gray-900">{after}</span>
      </span>
    );
  };

  const differsOn = (row: ComparedRow<T>, cols: Column<T>[]) =>
    !row.before || !row.after || cols.some(c => c.value(row.before as T) !== c.value(row.after as T));

  // Highlighted: any visible difference. Modified: added, removed or changed on a tracked column.
  const isChanged = (row: ComparedRow<T>) => compare && differsOn(row, columns);
  const isModified = (row: ComparedRow<T>) =>
    compare && differsOn(row, columns.filter(c => c.tracked !== false));

  // Comparing: only the modified lines by default, the others behind "Voir tout".
  const modifiedRows = rows.filter(isModified);
  const collapsible = compare && modifiedRows.length < rows.length;
  const visibleRows = compare && !showAll ? modifiedRows : rows;

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{t(`${k}.NONE`)}</p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-gray-500">{t(`${k}.NO_CHANGE`)}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                {columns.map(column => (
                  <th key={column.label} className="px-3 py-2 font-medium">{column.label}</th>
                ))}
                {compare && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => (
                <tr
                  key={row.key}
                  className={clsx('border-t', isChanged(row) && 'bg-yellow-50')}
                >
                  {columns.map((column, index) => (
                    <td key={column.label} className="px-3 py-2 text-gray-700">
                      {renderCell(row, column, index === 0)}
                    </td>
                  ))}
                  {compare && (
                    <td className="px-3 py-2 text-right text-xs font-medium">
                      {!row.before && <span className="text-green-700">{t(`${k}.ADDED`)}</span>}
                      {!row.after && <span className="text-red-600">{t(`${k}.REMOVED`)}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {collapsible && (
        <ToggleButton
          open={showAll}
          label={showAll ? t(`${k}.SHOW_CHANGES_ONLY`) : t(`${k}.SHOW_ALL`, { count: rows.length })}
          onClick={() => setShowAll(value => !value)}
        />
      )}
    </div>
  );
};

interface ValidationFeedbackPanelProps {
  demande: DemandeValidationDto;
  /** Overrides the status shown in the header (e.g. the validator's page, which also shows cancelled requests). */
  statusLabel?: string;
  /** Shows the variables table: internal data, for the validator only (hidden from the broker). */
  showVariables?: boolean;
}

/**
 * Validator's answer to the latest request: who and when, message, clause added to the
 * special conditions, and the quote before / after the validator's changes.
 */
export const ValidationFeedbackPanel: FC<ValidationFeedbackPanelProps> = ({
  demande,
  statusLabel,
  showVariables = false
}) => {
  const { t } = useTranslation(NsKey.Contractualization);
  const k = 'CONTRACTUALIZATION.CARDS.VALIDATION_FEEDBACK_PANEL';

  const before: DevisPhoto = demande.devisAvant;
  const after: DevisPhoto | undefined = demande.devisApres ?? undefined;
  const compare = after != null;
  const state = getValidationDisplayState(demande);
  // Without an "after" snapshot there is nothing to compare: the snapshot stays folded.
  const [showSnapshot, setShowSnapshot] = useState(false);
  const snapshotVisible = compare || showSnapshot;

  const snapshotTitle = compare
    ? t(`${k}.CHANGES_TITLE`)
    : state === 'VALIDATED'
      ? t(`${k}.UNCHANGED_TITLE`)
      : t(`${k}.SUBMITTED_TITLE`);

  const guaranteeColumns: Column<DevisPhotoGarantie>[] = [
    { label: t(`${k}.GUARANTEE`), value: g => g.designation || g.code || '—' },
    { label: t(`${k}.CEILING`), value: g => formatValidationNumber(g.plafond) },
    { label: t(`${k}.DEDUCTIBLE`), value: g => formatValidationNumber(g.franchise) },
    // The premium follows any re-pricing: a change on it alone is not the validator's change
    { label: t(`${k}.PREMIUM_TTC`), value: g => formatValidationAmount(g.primeTtc), tracked: false }
  ];
  const variableColumns: Column<DevisPhotoVariable>[] = [
    { label: t(`${k}.VARIABLE`), value: v => v.libelle || v.code || '—' },
    { label: t(`${k}.VALUE`), value: v => formatValidationNumber(v.valeur) }
  ];
  const feeColumns: Column<DevisPhotoFrais>[] = [
    { label: t(`${k}.FEE`), value: f => f.libelle || '—' },
    { label: t(`${k}.AMOUNT`), value: f => formatValidationAmount(f.montantTtc) }
  ];

  const totals: { label: string; before: number; after?: number }[] = [
    { label: t(`${k}.PRICE_HT`), before: before.prixHt, after: after?.prixHt },
    { label: t(`${k}.PRICE_TTC`), before: before.prixTtc, after: after?.prixTtc },
    { label: t(`${k}.CLIENT_PRICE`), before: before.prixClient, after: after?.prixClient }
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 rounded-xl border bg-white p-4 md:grid-cols-3">
        <div>
          <span className="text-sm font-medium text-gray-500">{t(`${k}.TITLE`)}</span>
          <p className="text-sm font-semibold text-gray-900">
            {statusLabel ?? t(`CONTRACTUALIZATION.CARDS.VALIDATION_STATUS.${state}`)}
          </p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">{t(`${k}.VALIDATOR`)}</span>
          <p className="text-sm text-gray-900">{demande.traiteePar || t(`${k}.UNKNOWN`)}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-500">{t(`${k}.ANSWERED_ON`)}</span>
          <p className="text-sm text-gray-900">
            {formatValidationDate(demande.dateReponse) || t(`${k}.UNKNOWN`)}
          </p>
        </div>
      </div>

      {demande.messageValideur && (
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-gray-900">{t(`${k}.VALIDATOR_MESSAGE`)}</h4>
          <p className="whitespace-pre-line rounded-lg border bg-white p-3 text-sm text-gray-700">
            {demande.messageValideur}
          </p>
        </div>
      )}

      {demande.clauseParticuliere && (
        <div className="flex flex-col gap-1 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <h4 className="text-sm font-semibold text-primary">{t(`${k}.CLAUSE`)}</h4>
          <p className="whitespace-pre-line text-sm text-gray-800">{demande.clauseParticuliere}</p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-primary">{snapshotTitle}</h3>

        {!compare && (
          <ToggleButton
            open={showSnapshot}
            label={
              showSnapshot
                ? t(`${k}.HIDE_SNAPSHOT`)
                : t(state === 'VALIDATED' ? `${k}.SHOW_VALIDATED_SNAPSHOT` : `${k}.SHOW_SUBMITTED_SNAPSHOT`)
            }
            onClick={() => setShowSnapshot(value => !value)}
          />
        )}

        {snapshotVisible && (
          <>
            <PhotoTable
              title={t(`${k}.GUARANTEES`)}
              rows={pairRows(before.garanties, after?.garanties, g => g.garantieId)}
              columns={guaranteeColumns}
              compare={compare}
            />
            {showVariables && (
              <PhotoTable
                title={t(`${k}.VARIABLES`)}
                rows={pairRows(before.variables, after?.variables, v => v.variableId)}
                columns={variableColumns}
                compare={compare}
              />
            )}
            <PhotoTable
              title={t(`${k}.FEES`)}
              rows={pairRows(before.frais, after?.frais, f => f.remunerationDevisId)}
              columns={feeColumns}
              compare={compare}
            />

            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-semibold text-gray-900">{t(`${k}.TOTALS`)}</h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {totals.map(total => {
                  const changed = compare && total.after !== total.before;
                  return (
                    <div
                      key={total.label}
                      className={clsx('rounded-xl border p-3 text-center', changed ? 'bg-yellow-50' : 'bg-white')}
                    >
                      <span className="text-sm font-medium text-gray-500">{total.label}</span>
                      {changed && total.after != null ? (
                        <p className="text-base">
                          <span className="text-gray-400 line-through">{formatValidationAmount(total.before)}</span>
                          {' → '}
                          <span className="font-bold text-gray-900">{formatValidationAmount(total.after)}</span>
                        </p>
                      ) : (
                        <p className="text-base font-bold text-gray-900">{formatValidationAmount(total.before)}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
